
import React, { useState } from 'react';
import Modal from 'react-modal';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { apiClient } from '../apiClient';
import { useTranslation } from 'react-i18next';

const AddCardModal = ({ isOpen, onRequestClose, onCardAdded, currentUser }) => {
    const { t } = useTranslation('customerInfo');
    const stripe = useStripe();
    const elements = useElements();
    const [error, setError] = useState(null);
    const [processing, setProcessing] = useState(false);

    const logCardSetupActivity = async (result, paymentMethodId, errorDetails) => {
        try {
            const token = await currentUser.getIdToken();
            await apiClient.post('/v1/log-card-setup-activity', {
                result,
                paymentMethodId,
                errorDetails
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
        } catch (apiError) {
            console.error('Failed to log card setup activity:', apiError);
            // Log reporting failure is not a user-facing error.
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setProcessing(true);
        setError(null);

        if (!stripe || !elements) {
            setError('Stripe.js has not yet loaded.');
            setProcessing(false);
            return;
        }

        const cardElement = elements.getElement(CardElement);

        try {
            const token = await currentUser.getIdToken();
            const setupIntentResult = await apiClient.post('/v1/setup-intent', {}, { 
                headers: { Authorization: `Bearer ${token}` } 
            });

            const { clientSecret } = await setupIntentResult.json();

            if (!clientSecret) {
                throw new Error('Failed to get a client secret from the server.');
            }

            const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
                payment_method: {
                    card: cardElement,
                },
            });

            if (stripeError) {
                throw stripeError; // Forward to the catch block for logging
            }

            if (setupIntent.status === 'succeeded') {
                // SUCCESS
                await logCardSetupActivity('success', setupIntent.payment_method, null);
                onCardAdded(setupIntent.payment_method);
                onRequestClose();
            } else {
                // Handle unexpected statuses
                throw new Error(`Card setup failed with unexpected status: ${setupIntent.status}`);
            }
        } catch (err) {
            // FAILURE
            setError(err.message || 'An unexpected error occurred.');
            await logCardSetupActivity('failed', null, err.message);
        } finally {
            setProcessing(false);
        }
    };

    const cardElementOptions = {
        style: {
            base: {
                fontSize: '16px',
                color: '#424770',
                '::placeholder': {
                    color: '#aab7c4',
                },
            },
            invalid: {
                color: '#9e2146',
            },
        },
        hidePostalCode: true,
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onRequestClose}
            contentLabel={t('addCardModalTitle')}
            className="fixed inset-0 flex items-center justify-center p-4 bg-black bg-opacity-50"
            overlayClassName="fixed inset-0 bg-black bg-opacity-50"
        >
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 relative">
                <h2 className="text-2xl font-bold mb-4 border-b pb-2">{t('addCardModalTitle')}</h2>
                <button onClick={onRequestClose} className="absolute top-4 right-4 text-gray-500 hover:text-gray-800">&times;</button>

                <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <label htmlFor="card-element" className="block text-sm font-medium text-gray-700 mb-2">
                            {t('addCardModalInstruction')}
                        </label>
                        <div className="p-3 border border-gray-300 rounded">
                            <CardElement id="card-element" options={cardElementOptions} />
                        </div>
                    </div>

                    {error && <div className="text-red-500 text-sm mb-4">{error}</div>}
                    
                    <p className="text-xs text-gray-500 mb-6">
                        {t('addCardModalAgreement')}
                    </p>

                    <button
                        type="submit"
                        disabled={!stripe || processing}
                        className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:bg-blue-300 transition-colors"
                    >
                        {processing ? t('addCardModalProcessing') : t('addCardModalSaveButton')}
                    </button>
                </form>
            </div>
        </Modal>
    );
};

export default AddCardModal;
